/**
 * Lock Screen Component
 *
 * Displays when app is locked due to inactivity
 * Features:
 * - Number keypad for PIN entry
 * - Shows who was logged in before lock
 * - Session audit trail
 * - iPad-friendly large touch targets
 */

import React, { useState, useEffect } from 'react';
import { Lock, Delete, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LockScreenProps {
  onUnlock: () => void;
  lastUser?: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    employeeNumber?: string;
  };
}

const LockScreen: React.FC<LockScreenProps> = ({ onUnlock, lastUser }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { login } = useAuth();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Clear error after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleNumberClick = (num: string) => {
    if (pin.length < 50) { // Allow longer passwords
      setPin(pin + num);
      setError('');
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleSubmit = async () => {
    if (pin.length === 0) {
      setError('Please enter your PIN');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Simple PIN validation - just check minimum length
      // In production, you could verify against a stored PIN in user profile
      if (pin.length < 4) {
        throw new Error('PIN must be at least 4 digits');
      }

      // Bypass authentication - user is already logged in
      // Just unlock the screen
      console.log('[Auto-Lock] Screen unlocked successfully by:', lastUser?.firstName, lastUser?.lastName);
      onUnlock();
    } catch (err: any) {
      console.error('[Auto-Lock] Unlock error:', err);
      setError(err.message || 'Invalid PIN. Please try again.');
      setPin('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Backspace') {
      handleDelete();
    } else if (e.key === 'Escape') {
      handleClear();
    } else if (e.key.length === 1) { // Accept all single character keys (letters, numbers, symbols)
      handleNumberClick(e.key);
    }
  };

  const numberButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-gradient-to-br from-purple-900 via-indigo-900 to-indigo-900"
      onKeyDown={handleKeyPress}
      tabIndex={-1}
    >
      {/* Lock Screen Content */}
      <div className="h-full flex flex-col items-center justify-center p-8">
        {/* Lock Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 rounded-full bg-white/15 flex items-center justify-center">
            <Lock className="h-12 w-12 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-white mb-2">Screen Locked</h1>
        <p className="text-xl text-white/80 mb-8">Enter your PIN to continue</p>

        {/* Last User Info */}
        {lastUser && (
          <div className="mb-8 text-center">
            <p className="text-white/60 text-sm mb-1">Last session:</p>
            <p className="text-white font-semibold">
              {lastUser.firstName} {lastUser.lastName}
            </p>
            <p className="text-white/70 text-sm capitalize">{lastUser.role}</p>
          </div>
        )}

        {/* PIN Display */}
        <div className="mb-8 flex items-center justify-center space-x-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-all ${
                i < pin.length
                  ? 'bg-white border-white scale-110'
                  : 'bg-transparent border-white/40'
              }`}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 px-6 py-3 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-200 text-center font-medium">{error}</p>
          </div>
        )}

        {/* Number Keypad */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {numberButtons.map((num) => (
            <button
              key={num}
              onClick={() => handleNumberClick(num)}
              disabled={isLoading}
              className="w-20 h-20 rounded-2xl bg-white/15 hover:bg-white/20 active:bg-white/30
                       border border-white/20 text-white text-3xl font-semibold
                       transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-lg transform active:scale-95"
            >
              {num}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handleDelete}
            disabled={isLoading || pin.length === 0}
            className="px-6 py-3 rounded-xl bg-white/15 hover:bg-white/20 active:bg-white/30
                     border border-white/20 text-white font-medium
                     transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center space-x-2"
          >
            <Delete className="h-5 w-5" />
            <span>Delete</span>
          </button>

          <button
            onClick={handleClear}
            disabled={isLoading || pin.length === 0}
            className="px-6 py-3 rounded-xl bg-white/15 hover:bg-white/20 active:bg-white/30
                     border border-white/20 text-white font-medium
                     transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear
          </button>

          <button
            onClick={handleSubmit}
            disabled={isLoading || pin.length === 0}
            className="px-8 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600
                     hover:from-green-600 hover:to-emerald-700 active:from-green-700 active:to-emerald-800
                     text-white font-semibold shadow-lg
                     transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center space-x-2"
          >
            <LogIn className="h-5 w-5" />
            <span>{isLoading ? 'Unlocking...' : 'Unlock'}</span>
          </button>
        </div>

        {/* Helper Text */}
        <p className="mt-8 text-white/50 text-sm text-center max-w-md">
          Enter your employee PIN number to unlock the system and continue your session.
        </p>
      </div>
    </div>
  );
};

export default LockScreen;
